<template>
  <TechLayoutContainer>
    <div class="retention-page config-page">
      <header class="page-header">
        <div>
          <span class="eyebrow">DATA RETENTION / POLICY CENTER</span>
          <h1>{{ langMap.retentionConfigTitle[lang] }}</h1>
          <p>{{ langMap.retentionConfigDesc[lang] }}</p>
        </div>
        <div class="header-actions">
          <Button ghost icon="ios-refresh" :loading="loading" @click="loadConfig">{{ langMap.refresh[lang] }}</Button>
          <Button type="primary" icon="ios-save-outline" :loading="saving" @click="saveConfig">{{ langMap.retentionSaveConfig[lang] }}</Button>
        </div>
      </header>

      <main class="page-grid">
        <section class="tech-panel policy-panel">
          <div class="panel-title">
            <div>
              <span class="panel-code">01 / RETENTION POLICY</span>
              <h2>{{ langMap.retentionPolicyTitle[lang] }}</h2>
            </div>
          </div>

          <Spin fix v-if="loading"></Spin>
          <Form ref="configForm" :model="form" :rules="rules" :label-width="0">
            <div class="form-grid">
              <FormItem v-for="item in retentionFields" :key="item.key" :prop="item.key" class="retention-field">
                <div class="field-title">
                  <div>
                    <span>{{ item.code }}</span>
                    <label>{{ item.label }}</label>
                  </div>
                  <Icon :type="item.icon" />
                </div>
                <InputNumber v-model="form[item.key]" :min="1" :precision="0"></InputNumber>
                <em>{{ langMap.retentionDayUnit[lang] }}</em>
              </FormItem>
            </div>

            <div class="guard-grid">
              <FormItem prop="storageThreshold" class="guard-field">
                <div class="guard-copy">
                  <span class="panel-code">02</span>
                  <div><h3>{{ langMap.retentionStorageThreshold[lang] }}</h3><p>{{ langMap.retentionStorageThresholdDesc[lang] }}</p></div>
                </div>
                <div class="guard-input"><InputNumber v-model="form.storageThreshold" :min="1" :max="100" :precision="0"></InputNumber><b>%</b></div>
              </FormItem>
              <FormItem prop="capacityMinDays" class="guard-field">
                <div class="guard-copy">
                  <span class="panel-code">03</span>
                  <div><h3>{{ langMap.retentionCapacityMinDays[lang] }}</h3><p>{{ langMap.retentionCapacityMinDaysDesc[lang] }}</p></div>
                </div>
                <div class="guard-input"><InputNumber v-model="form.capacityMinDays" :min="1" :precision="0"></InputNumber><b>{{ langMap.retentionDayUnit[lang] }}</b></div>
              </FormItem>
            </div>
          </Form>

          <footer class="panel-footer">
            <span>{{ updateSummary }}</span>
            <Button size="small" ghost icon="ios-undo" :disabled="!hasDefaults" @click="restoreDefaults">{{ langMap.retentionRestoreDefaults[lang] }}</Button>
          </footer>
        </section>

        <aside class="tech-panel overview-panel">
          <div class="panel-title">
            <div><span class="panel-code">LIVE / OVERVIEW</span><h2>{{ langMap.retentionOverview[lang] }}</h2></div>
            <Icon type="ios-analytics-outline" />
          </div>
          <div class="threshold-card">
            <div><span>{{ langMap.retentionTriggerLine[lang] }}</span><strong>{{ form.storageThreshold || '--' }}%</strong></div>
            <Progress :percent="storagePercent" :stroke-width="8" status="active" hide-info></Progress>
            <p>{{ langMap.retentionOverviewDesc[lang] }}</p>
          </div>
          <div class="strategy-list">
            <div v-for="item in retentionFields" :key="item.key" class="strategy-row">
              <i></i><div><span>{{ item.label }}</span><small>{{ item.code }}</small></div><strong>{{ form[item.key] || '--' }} {{ langMap.retentionDayUnit[lang] }}</strong>
            </div>
          </div>
          <div class="safe-line"><Icon type="ios-shield-outline" /><div><span>{{ langMap.retentionSafeLine[lang] }}</span><strong>{{ langMap.retentionKeepAtLeast[lang] }} {{ form.capacityMinDays || '--' }} {{ langMap.retentionDayUnit[lang] }}</strong></div></div>
        </aside>
      </main>
    </div>
  </TechLayoutContainer>
</template>

<script>
import TechLayoutContainer from '@/components/TechLayoutContainer'
import api from '@/api'
import langMap from '@/utils/language'
import { mapState } from 'vuex'

const emptyForm = () => ({
  monitorDays: null,
  commandDays: null,
  sentimentDays: null,
  videoDays: null,
  storageThreshold: null,
  capacityMinDays: null
})

export default {
  name: 'DataRetentionConfig',
  components: { TechLayoutContainer },
  data () {
    const currentLang = this.$store.state.app.language
    const positiveInteger = (rule, value, callback) => {
      callback(Number.isInteger(value) && value >= 1 ? undefined : new Error(langMap.retentionPositiveInteger[currentLang]))
    }
    const threshold = (rule, value, callback) => {
      callback(Number.isInteger(value) && value >= 1 && value <= 100 ? undefined : new Error(langMap.retentionThresholdRange[currentLang]))
    }
    return {
      langMap: langMap,
      loading: false,
      saving: false,
      updateUser: null,
      updateTime: '',
      form: emptyForm(),
      defaults: {},
      rules: {
        monitorDays: [{ validator: positiveInteger, trigger: 'change' }],
        commandDays: [{ validator: positiveInteger, trigger: 'change' }],
        sentimentDays: [{ validator: positiveInteger, trigger: 'change' }],
        videoDays: [{ validator: positiveInteger, trigger: 'change' }],
        storageThreshold: [{ validator: threshold, trigger: 'change' }],
        capacityMinDays: [{ validator: positiveInteger, trigger: 'change' }]
      }
    }
  },
  computed: {
    ...mapState({
      lang: state => state.app.language
    }),
    retentionFields () {
      return [
        { key: 'monitorDays', label: langMap.retentionMonitorDays[this.lang], code: 'MONITOR', icon: 'ios-pulse' },
        { key: 'commandDays', label: langMap.retentionCommandDays[this.lang], code: 'COMMAND', icon: 'ios-send' },
        { key: 'sentimentDays', label: langMap.retentionSentimentDays[this.lang], code: 'ALERT', icon: 'ios-warning-outline' },
        { key: 'videoDays', label: langMap.retentionVideoDays[this.lang], code: 'VIDEO', icon: 'ios-videocam-outline' }
      ]
    },
    updateSummary () {
      if (!this.updateTime) return langMap.retentionShowingDefaults[this.lang]
      const user = this.updateUser ? '（' + langMap.retentionUpdateUser[this.lang] + this.updateUser + '）' : ''
      return langMap.retentionLastUpdate[this.lang] + this.updateTime + user
    },
    storagePercent () {
      const value = Number(this.form.storageThreshold)
      return Number.isInteger(value) && value >= 1 && value <= 100 ? value : 0
    },
    hasDefaults () {
      return Object.keys(this.defaults).length === 6 && Object.keys(this.defaults).every(key => Number.isInteger(this.defaults[key]))
    }
  },
  created () {
    this.loadConfig()
  },
  methods: {
    validNumber (value, fallback = null) {
      if (value === null || value === undefined || value === '') return fallback
      const number = Number(value)
      return Number.isInteger(number) && number >= 1 ? number : fallback
    },
    clearValidation () {
      this.$nextTick(() => this.$refs.configForm && this.$refs.configForm.clearValidate())
    },
    loadConfig () {
      this.loading = true
      api.getData('system-server/data-retention/config', this).then(res => {
        if (res.state === 0) {
          this.$Notice.error({ title: langMap.retentionLoadFailed[this.lang], desc: res.msg })
          return
        }
        const config = res.data || {}
        const fallback = emptyForm()
        this.defaults = {
          monitorDays: this.validNumber(config.defaultMonitorDays, fallback.monitorDays),
          commandDays: this.validNumber(config.defaultCommandDays, fallback.commandDays),
          sentimentDays: this.validNumber(config.defaultSentimentDays, fallback.sentimentDays),
          videoDays: this.validNumber(config.defaultVideoDays, fallback.videoDays),
          storageThreshold: this.validNumber(config.defaultStorageThreshold, fallback.storageThreshold),
          capacityMinDays: this.validNumber(config.defaultCapacityMinDays, fallback.capacityMinDays)
        }
        this.form = {
          monitorDays: this.validNumber(config.monitorDays, this.defaults.monitorDays),
          commandDays: this.validNumber(config.commandDays, this.defaults.commandDays),
          sentimentDays: this.validNumber(config.sentimentDays, this.defaults.sentimentDays),
          videoDays: this.validNumber(config.videoDays, this.defaults.videoDays),
          storageThreshold: this.validNumber(config.storageThreshold, this.defaults.storageThreshold),
          capacityMinDays: this.validNumber(config.capacityMinDays, this.defaults.capacityMinDays)
        }
        this.updateUser = config.updateUser || null
        this.updateTime = config.updateTime || ''
        this.clearValidation()
      }).finally(() => { this.loading = false })
    },
    restoreDefaults () {
      this.form = Object.assign({}, this.defaults)
      this.clearValidation()
      this.$Message.info(langMap.retentionDefaultsRestored[this.lang])
    },
    saveConfig () {
      this.$refs.configForm.validate(valid => {
        if (!valid) return
        this.saving = true
        api.postData('system-server/data-retention/config', this.form, this).then(res => {
          if (res.state === 0) {
            this.$Notice.error({ title: langMap.saveFailed[this.lang], desc: res.msg })
            return
          }
          this.$Notice.success({ title: langMap.saveSuccess[this.lang], desc: res.msg || langMap.retentionConfigUpdated[this.lang] })
          this.loadConfig()
        }).finally(() => { this.saving = false })
      })
    }
  }
}
</script>

<style lang="less" scoped>
.retention-page { display: flex; flex-direction: column; width: 100%; height: 100%; min-height: 0; overflow: hidden; box-sizing: border-box; padding: 26px 30px 30px; color: #e9f7ff; }
.page-header, .header-actions, .panel-title, .field-title, .guard-copy, .panel-footer, .strategy-row, .safe-line { display: flex; align-items: center; }
.page-header { flex-shrink: 0; justify-content: space-between; gap: 24px; margin-bottom: 22px; }
.eyebrow, .panel-code { color: #5edcff; font-size: 11px; font-weight: 600; letter-spacing: 1.4px; }
h1, h2, h3, p { margin: 0; }
h1 { margin-top: 6px; color: #fff; font-size: 26px; letter-spacing: 1px; }
.page-header p, .guard-copy p, .threshold-card p { margin-top: 8px; color: rgba(198,225,238,.68); font-size: 13px; }
.header-actions { gap: 10px; flex-shrink: 0; }
.header-actions /deep/ .ivu-btn-ghost, .panel-footer /deep/ .ivu-btn-ghost { border-color: rgba(92,220,255,.5); color: #a8ecff; }
.header-actions /deep/ .ivu-btn-primary { border-color: #23b8e8; background: linear-gradient(135deg,#118bb8,#25c8f4); box-shadow: 0 5px 16px rgba(0,190,255,.22); }
.page-grid { display: grid; flex: 1; grid-template-columns: minmax(0,1.8fr) minmax(0,.9fr); gap: 18px; min-height: 0; }
.tech-panel { position: relative; overflow: hidden; border: 1px solid rgba(85,210,255,.24); border-radius: 10px; background: linear-gradient(145deg,rgba(12,43,72,.84),rgba(7,24,50,.88)); box-shadow: inset 0 1px 0 rgba(143,233,255,.08),0 12px 26px rgba(0,0,0,.16); }
.tech-panel::before { position: absolute; top: 0; left: 0; width: 100%; height: 2px; content: ''; background: linear-gradient(90deg,transparent,#4adeff,transparent); }
.policy-panel, .overview-panel { padding: 18px; }
.policy-panel { display: flex; flex-direction: column; min-height: 0; }
.overview-panel { display: flex; flex-direction: column; min-height: 0; }
.panel-title { justify-content: space-between; gap: 16px; margin-bottom: 14px; }
.panel-title h2 { margin-top: 5px; color: #fff; font-size: 18px; }
.panel-title > .ivu-icon { color: #50d9ff; font-size: 30px; }
.form-grid, .guard-grid { display: grid; grid-template-columns: repeat(2,minmax(0,1fr)); gap: 14px; }
.retention-field, .guard-field { position: relative; margin: 0; padding: 12px; border: 1px solid rgba(92,213,255,.16); border-radius: 7px; background: rgba(1,21,43,.38); }
.retention-field:hover, .guard-field:hover { border-color: rgba(92,213,255,.44); }
.field-title { justify-content: space-between; margin-bottom: 10px; }
.field-title span, .strategy-row small { display: block; color: #5edcff; font-size: 10px; letter-spacing: 1px; }
.field-title label { display: block; margin-top: 5px; color: #dff7ff; font-size: 14px; }
.field-title /deep/ .ivu-icon { color: #4ddaff; font-size: 22px; }
.retention-field /deep/ .ivu-input-number, .guard-input /deep/ .ivu-input-number { width: ~"calc(100% - 30px)"; border-color: rgba(91,211,255,.42) !important; background: rgba(2,20,40,.72) !important; box-shadow: 0 0 0 1px rgba(91,211,255,.05), inset 0 0 14px rgba(20,170,220,.08); }
.retention-field /deep/ .ivu-input-number-input, .guard-input /deep/ .ivu-input-number-input { height: 34px; color: #e8fbff !important; background: transparent !important; font-size: 17px; font-weight: 600; }
.retention-field /deep/ .ivu-input-number-handler-wrap, .guard-input /deep/ .ivu-input-number-handler-wrap { border-left-color: rgba(91,211,255,.28) !important; background: rgba(8,54,83,.9) !important; }
.retention-field /deep/ .ivu-input-number-handler, .guard-input /deep/ .ivu-input-number-handler { color: #7feaff !important; }
.retention-field em { position: absolute; right: 18px; bottom: 25px; color: #79dff8; font-size: 13px; font-style: normal; }
.guard-grid { margin-top: 10px; }
.guard-field { min-height: 100px; }
.guard-copy { align-items: flex-start; gap: 11px; padding-right: 92px; }
.guard-copy h3 { color: #e9faff; font-size: 15px; }
.guard-copy p { margin-top: 5px; line-height: 1.45; }
.guard-input { position: absolute; right: 12px; bottom: 12px; width: 104px; }
.guard-input /deep/ .ivu-input-number { width: 72px; }
.guard-input b { margin-left: 7px; color: #78ddf8; font-weight: 400; }
.panel-footer { flex-shrink: 0; justify-content: space-between; gap: 12px; margin-top: 12px; padding-top: 10px; border-top: 1px solid rgba(97,217,255,.14); color: rgba(204,232,243,.68); font-size: 12px; }
.threshold-card { flex-shrink: 0; padding: 12px; border: 1px solid rgba(95,221,255,.2); border-radius: 8px; background: rgba(1,18,38,.48); }
.threshold-card > div { display: flex; justify-content: space-between; align-items: center; gap: 12px; margin-bottom: 12px; color: #d8f8ff; font-size: 13px; }
.threshold-card > div span { min-width: 0; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
.threshold-card strong { color: #5de1ff; font-size: 26px; }
.threshold-card /deep/ .ivu-progress-bg { background: linear-gradient(90deg,#1ec9ef,#79f1ff) !important; }
.threshold-card /deep/ .ivu-progress-inner { background: rgba(69,160,202,.18); }
.strategy-list { flex: 1; min-height: 0; margin-top: 10px; border-top: 1px solid rgba(98,215,255,.14); }
.strategy-row { gap: 10px; padding: 9px 2px; border-bottom: 1px solid rgba(98,215,255,.12); }
.strategy-row i { width: 7px; height: 7px; border-radius: 50%; background: #57e3ff; box-shadow: 0 0 10px #57e3ff; }
.strategy-row div { flex: 1; }
.strategy-row span { display: block; color: #d7f5fc; font-size: 13px; }
.strategy-row strong { color: #83eaff; font-size: 15px; }
.safe-line { flex-shrink: 0; gap: 11px; min-width: 0; margin-top: 10px; padding: 10px 12px; overflow: hidden; border: 1px solid rgba(119,241,186,.22); border-radius: 8px; background: rgba(24,125,102,.12); }
.safe-line /deep/ .ivu-icon { color: #6cf4bf; font-size: 26px; }
.safe-line > div { min-width: 0; }.safe-line span, .safe-line strong { display: block; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
.safe-line span { color: rgba(215,255,240,.7); font-size: 12px; }
.safe-line strong { margin-top: 3px; color: #a9ffdb; font-size: 14px; }
.config-page /deep/ .ivu-form-item-error-tip { color: #ff9da4; font-size: 11px; }
@media (max-width: 700px) { .retention-page { height: auto; min-height: 100%; overflow: hidden; padding: 18px; } .page-header, .panel-footer { align-items: flex-start; flex-direction: column; } .page-grid { height: auto; grid-template-columns: 1fr; } .form-grid, .guard-grid { grid-template-columns: 1fr; } .header-actions { width: 100%; } .header-actions /deep/ .ivu-btn { flex: 1; } }
</style>
